import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Rocket, TrendingUp, Compass, Users, CheckCircle2, Infinity as InfinityIcon, Sun, Gift, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import OnboardingModal from "@/components/OnboardingModal";
import SEO from "@/components/SEO";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_ai-business-academy-2/artifacts/c1jkvwwp_4185C05A-6A9A-42F7-A146-9109CF6F03DD.png";

const PILLARS = [
  { icon: Sparkles, title: "AI-Personalized", body: "Learning paths tailored to your goals, pace, and style." },
  { icon: Rocket, title: "Real-World Skills", body: "Practical knowledge for real impact. Not theory." },
  { icon: TrendingUp, title: "Track & Grow", body: "See your progress. Celebrate your wins. Build the streak." },
  { icon: Users, title: "Expert Guidance", body: "Learn from industry leaders and your AI tutor, 24/7." },
];

const SYMBOLISM = [
  { icon: TrendingUp, title: "Rise", body: "Ascending to your highest potential." },
  { icon: Sun, title: "Light", body: "Knowledge that illuminates." },
  { icon: InfinityIcon, title: "Transformation", body: "Continuous growth and becoming." },
  { icon: Compass, title: "Guidance", body: "AI-powered guidance every step." },
];

const MODELS = ["GPT-5.2", "Claude 4.5", "Gemini 3", "Nano Banana", "Sora 2", "ElevenLabs", "Perplexity", "Midjourney", "Veo 3", "Whisper", "Cursor", "Suno"];

// Fallback tiers so the teaser section always renders, even if /pricing is slow or fails.
const FALLBACK_TIERS = [
  {
    id: "ascender", name: "Ascender", price_monthly: 9.99, price_annual: 99.0,
    blurb: "Start your ascent. The essentials.",
    features: ["AI Fundamentals path (8 lessons)", "Unlimited AI Tutor (Claude 4.5)", "Browse all 22 AI models", "Daily streak + XP tracking"],
  },
  {
    id: "pathfinder", name: "Pathfinder", price_monthly: 19.99, price_annual: 199.0,
    blurb: "For serious learners forging the way.",
    features: ["Everything in Ascender, plus:", "7 additional paths", "Pro: Prompt Engineering Mastery", "Pro: AI Automation Stack", "XP + leaderboards"],
    highlight: true,
  },
  {
    id: "sage", name: "Sage", price_monthly: 29.99, price_annual: 299.0,
    blurb: "Master the craft. Build the business.",
    features: ["Everything in Pathfinder, plus:", "Sage: AI-First Startup Playbook", "Sage: AI Sales & Marketing Engine", "Real founder case studies", "Lifetime price lock"],
  },
  {
    id: "business", name: "Business", price_monthly: 149.0, price_annual: 1490.0,
    founding_price_monthly: 99.0, founding_price_annual: 990.0,
    blurb: "Equip your team to learn, govern, and apply AI across everyday business operations.",
    features: [
      "Up to 5 team members",
      "Business-focused AI learning paths",
      "Employee progress & certificate tracking",
      "Shared company prompt library",
      "Business templates & SOPs",
      "AI-readiness assessment",
    ],
  },
];

export default function Landing() {
  const nav = useNavigate();
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [seoPages, setSeoPages] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  useEffect(() => {
    api.get("/pricing").then((r) => { if (r?.tiers?.length) setTiers(r.tiers); }).catch(() => {});
    api.get("/seo/published").then((r) => { setSeoPages((r.pages || []).filter((p) => p.kind === "hub")); }).catch(() => {});
  }, []);

  // Auto-popup the personalization quiz for first-time anonymous visitors.
  // Skipped if: user is signed in, already dismissed within 7 days, or already took quiz.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const isLoggedIn = !!localStorage.getItem("ascendra_token");
      if (isLoggedIn) return;
      const dismissed = localStorage.getItem("ascendra_quiz_dismissed_at");
      if (dismissed) {
        const days = (Date.now() - Number(dismissed)) / (1000 * 60 * 60 * 24);
        if (days < 7) return;
      }
      const completed = localStorage.getItem("ascendra_quiz_completed_at");
      if (completed) return;
    } catch (_e) { /* localStorage blocked — show anyway */ }
    const t = setTimeout(() => setQuizOpen(true), 8000); // 8s delay
    return () => clearTimeout(t);
  }, []);

  // Persist quiz close as "dismissed" if user closes before submitting; or
  // as "completed" if they finished. Either way, suppress for 7 days.
  const handleQuizOpenChange = (open) => {
    setQuizOpen(open);
    if (!open) {
      try {
        if (!localStorage.getItem("ascendra_quiz_completed_at")) {
          localStorage.setItem("ascendra_quiz_dismissed_at", String(Date.now()));
        }
      } catch (_e) { /* ignore */ }
    }
  };

  const handleQuizComplete = () => {
    try { localStorage.setItem("ascendra_quiz_completed_at", String(Date.now())); } catch (_e) { /* ignore */ }
  };

  return (
    <div data-testid="landing-page">
      <SEO
        title="Learn AI in 2026 — Interactive paths, AI tutor, certificates"
        description="Ascendra Academy teaches modern AI literacy through 15-day challenges, hands-on prompt playgrounds, and an AI tutor. Master GPT-5.2, Claude Sonnet 4.5, Gemini 3, Sora 2, and more."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          {/* Left — brand image */}
          <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-auto lg:h-[640px] rounded-3xl overflow-hidden order-2 lg:order-1" style={{ background: "#0A0413" }}>
            <img src={HERO_IMG} alt="Ascendra brand" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(120deg, rgba(10,4,19,0) 50%, rgba(10,4,19,0.85) 100%)" }} />
          </div>

          {/* Right — copy */}
          <div className="order-1 lg:order-2">
            <div className="asc-kicker">AI-Powered Learning · Boundless Growth</div>
            <h1 className="asc-h1 text-5xl sm:text-6xl lg:text-7xl mt-6">
              Ready to <span className="asc-gradient-text italic">rise today?</span>
            </h1>
            <p className="text-[var(--asc-text-dim)] text-lg mt-6 max-w-xl leading-relaxed">
              The AI learning partner that walks beside you — from your first prompt to your first launch.
              Beginner to advanced, taught through every model that matters in 2026.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => nav("/signup")} className="asc-btn-primary text-base" data-testid="hero-cta-btn">
                Begin your ascent <ArrowRight size={18} />
              </button>
              <button
                onClick={() => setQuizOpen(true)}
                className="asc-btn-secondary text-base"
                data-testid="hero-quiz-btn"
                style={{ color: "#FFB000", borderColor: "rgba(255,176,0,0.55)" }}
              >
                <Wand2 size={16} /> Find my path (60s)
              </button>
              <button onClick={() => nav("/pricing")} className="asc-btn-secondary text-base" data-testid="hero-pricing-btn">See pricing</button>
            </div>
            <button onClick={() => setLeadOpen(true)} className="mt-5 inline-flex items-center gap-2 text-sm text-[var(--asc-brand)] hover:underline font-bold" data-testid="hero-lead-magnet-btn">
              <Gift size={14} /> Or grab the free AI Roadmap (no signup)
            </button>
          </div>
        </div>
      </section>

      <LeadCaptureModal open={leadOpen} onClose={() => setLeadOpen(false)} source="landing_hero" />
      <OnboardingModal
        open={quizOpen}
        onOpenChange={handleQuizOpenChange}
        anonymous
        source="landing_popup"
        onComplete={handleQuizComplete}
      />

      {/* MISSION */}
      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto relative overflow-hidden rounded-3xl border border-[var(--asc-border-strong)] p-10 sm:p-14"
          style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.18), rgba(255,176,0,0.06))" }}>
          <div className="asc-kicker">Our Mission</div>
          <p className="asc-h2 text-2xl sm:text-4xl mt-5">
            To <span className="text-[var(--asc-brand)]">empower</span> every learner to{' '}
            <span className="text-[var(--asc-brand)] italic">rise beyond limits</span>{' '}
            through AI-driven education and human potential.
          </p>
        </div>
      </section>

      {/* MODELS MARQUEE */}
      <section className="border-y border-[var(--asc-border)] py-10" style={{ background: "#1A1F3D" }}>
        <div className="asc-label text-center mb-5">Covering the models that matter</div>
        <div className="overflow-hidden">
          <div className="marquee-track flex gap-3 w-max">
            {[...MODELS, ...MODELS].map((m, i) => (
              <span key={i} className="px-4 py-2 rounded-full border border-[var(--asc-border-strong)] text-sm text-[var(--asc-text-dim)] font-bold" style={{ background: "#0A0413" }}>{m}</span>
            ))}
          </div>
        </div>
        {seoPages.length > 0 && (
          <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-7 text-center" data-testid="landing-seo-links">
            <div className="asc-label mb-3">Explore the AI models we teach</div>
            <div className="flex flex-wrap justify-center gap-2">
              {seoPages.slice(0, 12).map((p) => (
                <Link key={p.id} to={`/learn/${p.model_slug}`} data-testid={`landing-seo-link-${p.model_slug}`}
                  className="px-3 py-1.5 rounded-full text-xs font-bold border border-[var(--asc-border)] text-[var(--asc-text-dim)] hover:text-white hover:border-[var(--asc-brand)] transition">
                  {p.model_name || p.model_slug}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* PILLARS */}
      <Section kicker="Built for your rise" title="Everything you need to ascend.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="asc-card p-6">
              <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: "rgba(255,176,0,0.12)", border: "1px solid rgba(255,176,0,0.35)" }}>
                <Icon size={22} color="#FFB000" />
              </div>
              <div className="asc-kicker mb-1">{title}</div>
              <p className="text-[var(--asc-text-dim)] text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRICING TEASER */}
      <Section kicker="Pricing" title="Start free. Rise on your terms.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-pricing-teaser">
          {tiers.map((t) => (
            <div key={t.id} data-testid={`landing-tier-${t.id}`} className={`asc-card p-7 relative ${t.highlight ? "border-[var(--asc-brand)]" : ""}`}
              style={t.highlight ? { borderColor: "#FFB000", background: "rgba(255,176,0,0.04)" } : {}}>
              {t.highlight && (
                <div className="absolute -top-2.5 left-6 px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider" style={{ background: "#FFB000", color: "#000" }}>MOST POPULAR</div>
              )}
              <div className="asc-h2 text-2xl">{t.name}</div>
              <div className="flex items-end gap-1 mt-2">
                <span className="text-4xl font-black">${t.price_monthly}</span>
                <span className="text-[var(--asc-text-dim)] text-sm pb-2">/mo</span>
              </div>
              <div className="text-xs text-[var(--asc-text-muted)] mt-0.5">or ${t.price_annual}/yr</div>
              <p className="text-[var(--asc-text-dim)] text-sm mt-2">{t.blurb}</p>
              <ul className="mt-5 space-y-2">
                {t.features.slice(0, 5).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--asc-text-dim)]">
                    <CheckCircle2 size={16} className={t.highlight ? "text-[var(--asc-brand)] mt-0.5" : "text-[var(--asc-lavender)] mt-0.5"} /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => nav("/signup")} data-testid={`landing-cta-${t.id}`} className={`w-full mt-6 py-3 rounded-xl font-bold ${t.highlight ? "" : "border"}`} style={t.highlight ? { background: "#FFB000", color: "#000" } : { background: "#1F183A", color: "#fff", borderColor: "rgba(191,180,255,0.28)" }}>
                Choose {t.name}
              </button>
            </div>
          ))}
        </div>
        <div className="text-center mt-6 text-sm text-[var(--asc-text-muted)]">
          <Link to="/pricing" className="text-[var(--asc-brand)] hover:underline">See all features & annual pricing →</Link>
        </div>
      </Section>

      {/* SYMBOLISM */}
      <Section kicker="The name means" title="Rise. Light. Transformation. Guidance.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SYMBOLISM.map(({ icon: Icon, title, body }) => (
            <div key={title} className="asc-card p-6">
              <div className="w-12 h-12 rounded-full grid place-items-center mb-4" style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(191,180,255,0.35)" }}>
                <Icon size={24} color="#FFB000" />
              </div>
              <h3 className="asc-h2 text-lg mb-1">{title}</h3>
              <p className="text-[var(--asc-text-dim)] text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="relative px-6 py-20 mx-6 my-16 rounded-3xl overflow-hidden border border-[var(--asc-border-strong)]" style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.18), rgba(255,176,0,0.06))" }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="asc-kicker">Your ascent begins now</div>
          <div className="mt-4 flex flex-wrap justify-center items-center gap-3 sm:gap-5">
            {["LEARN.", "GROW.", "TRANSFORM."].map((w) => (
              <span key={w} className="text-2xl sm:text-3xl font-black tracking-wider">{w}</span>
            ))}
            <span className="text-2xl sm:text-3xl font-black tracking-wider asc-gradient-text italic">ASCEND.</span>
          </div>
          <p className="text-[var(--asc-text-dim)] mt-5">Free to start. No card required.</p>
          <button onClick={() => nav("/signup")} data-testid="final-cta-btn" className="asc-btn-primary mt-6 text-base">Begin your ascent <ArrowRight size={18} /></button>
        </div>
      </section>
    </div>
  );
}

function Section({ kicker, title, children }) {
  return (
    <section className="px-6 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto">
        <div className="asc-kicker">{kicker}</div>
        <h2 className="asc-h2 text-3xl sm:text-5xl mt-3 mb-10 max-w-3xl">{title}</h2>
        {children}
      </div>
    </section>
  );
}
